import { createSelector } from '@ngxs/store';
import { IntakeFormClientResponsesSelectors } from '@domain/intake-form-client-responses';
import { MatterClientIntakeFormState } from '@state/matter/client-intake/client-intake.state';
import { SelectedMatter } from 'src/app/shared/state/matter/matter.selectors';
import { groupItemsIntoSections } from '../utils/response-helpers';
import {
	ClientIntakeForm,
	IntakeFormResponse,
	FieldResponseStatus,
	FormFileMetadata,
	MapTo,
	ExtraMapToPurpose,
	Form,
	FormField,
	FormSubSection,
	UploadConfig,
} from '@models/client-intake';
import { Matter, PersonMatterClient } from '@models/matter';
import { PersonClient, BusinessClient } from '@models/clients';
import {
	FormOption,
	ReviewResponseItem,
	IntakeResponseSection,
	FormViewData,
	ReviewResponsesViewModel,
} from './models';
import { MatterClients } from 'src/app/shared/state/global/matter_clients';
import {
	extractValueFromMapTo,
	PortalEngineSelectors,
	getDisplayValue,
	DefaultValueContext,
} from '@domain/portal-engine';
import { PortalEngineData } from 'libs/domain/client-intake/portal-engine/src/state/portal-engine.models';
import {
	EngineFormItemFormField,
	EngineFormItemType,
} from '@models/client-portal/engine-client-intake';

// Move to domain utils
function findFieldInForm(
	form: Form,
	questionId: string,
): {
	mapTo?: MapTo;
	uploadOnlyConfig?: UploadConfig | null;
	sectionTitle: string;
	sectionOrder: number;
	fieldOrder: number;
} | null {
	for (
		let sectionIndex = 0;
		sectionIndex < form.sections.length;
		sectionIndex++
	) {
		const section = form.sections[sectionIndex];
		const result = findFieldInItems(
			section.fields,
			questionId,
			section.title,
			section.order ?? sectionIndex,
		);
		if (result) {
			return result;
		}
	}
	return null;
}

// Move to domain utils
function findFieldInItems(
	items: (FormField | FormSubSection)[],
	questionId: string,
	sectionTitle: string,
	sectionOrder: number,
): {
	mapTo?: MapTo;
	uploadOnlyConfig?: UploadConfig | null;
	sectionTitle: string;
	sectionOrder: number;
	fieldOrder: number;
} | null {
	for (const item of items) {
		if ('type' in item && item.id === questionId) {
			return {
				mapTo: item.mapTo,
				uploadOnlyConfig: item.uploadOnlyConfig,
				sectionTitle,
				sectionOrder,
				fieldOrder: item.order ?? 0,
			};
		}

		// If it has fields array, it's a subsection - recurse
		if ('fields' in item && Array.isArray(item.fields)) {
			const result = findFieldInItems(
				item.fields,
				questionId,
				sectionTitle,
				sectionOrder,
			);
			if (result) {
				return result;
			}
		}
	}
	return null;
}

export const FormOptionsSelector = createSelector(
	[IntakeFormClientResponsesSelectors.$intakeFormResponses],
	(responses: IntakeFormResponse[]) => {
		const formMap = new Map<string, FormOption>();

		responses.forEach((response) => {
			if (response.id && response.formAnswers) {
				const responseCount = Object.values(response.formAnswers).filter(
					(answer) => answer.status === FieldResponseStatus.READY_TO_REVIEW,
				).length;

				const existing = formMap.get(response.id);
				if (existing) {
					existing.responseCount += responseCount;
				} else {
					formMap.set(response.id, {
						formId: response.id,
						formName: response.formName,
						responseCount: responseCount,
						type: response.type,
					});
				}
			}
		});

		const formOptions = Array.from(formMap.values());

		// Sort: Client forms first (alphabetically), then general forms at the end
		const clientFormOptions = formOptions.filter(
			(opt) => opt.type === 'client',
		);
		const generalFormOptions = formOptions.filter(
			(opt) => opt.type === 'general',
		);
		clientFormOptions.sort((a, b) => a.formName.localeCompare(b.formName));
		generalFormOptions.sort((a, b) => a.formName.localeCompare(b.formName));

		return [...clientFormOptions, ...generalFormOptions];
	},
);

export const AllResponseItemsSelector = createSelector(
	[
		SelectedMatter.getSelected,
		MatterClients.get_matterClientsWithAssociationMapped,
		PortalEngineSelectors.$engineGeneratedForms,
		PortalEngineSelectors.$engineData,
	],
	(
		matter: Matter,
		clients: PersonClient[],
		engineForms: Form[] | null,
		engineData: PortalEngineData | null,
	): ReviewResponseItem[] => {
		const items: ReviewResponseItem[] = [];

		if (!engineData) {
			return items;
		}

		const { engineItems, context, currentResponses } = engineData;
		if (
			!engineItems ||
			!context ||
			!currentResponses ||
			!clients ||
			!engineForms
		) {
			return items;
		}

		const engineFieldItems = engineItems.filter(
			(item) => item.type === EngineFormItemType.FIELD,
		);

		currentResponses.forEach((formResponse) => {
			if (formResponse.id && formResponse.formAnswers) {
				const correspondingForm = engineForms.find((form) => {
					if (formResponse.type === 'general') {
						return form.id === context.general?.id;
					} else {
						return form.clientId === formResponse.clientId;
					}
				});

				if (!correspondingForm) {
					console.warn(
						`No engine form found for response type: ${formResponse.type}, clientId: ${formResponse.clientId}`,
					);
					return;
				}

				Object.entries(formResponse.formAnswers).forEach(
					([questionId, answer]) => {
						const fieldMetadata = findFieldInForm(
							correspondingForm,
							questionId,
						);

						if (!fieldMetadata) {
							console.warn(`Field ${questionId} not found in engine form`);
							return;
						}

						const clientResponse =
							typeof answer.answer === 'object'
								? (answer.answer as FormFileMetadata)
								: String(answer.answer || '');

						const engineFieldItemOptions = (
							engineFieldItems?.find(
								(item) => item.item.id === answer.questionId,
							)?.item as EngineFormItemFormField
						)?.options;

						const targetClient = clients.find(
							(client) => client.id === formResponse.clientId,
						);
						const defaultValueContext: DefaultValueContext = {
							matter,
							client: targetClient as PersonClient,
							businessClient: targetClient as unknown as BusinessClient,
							matterClient: targetClient as PersonMatterClient,
						};

						const matterDetails = extractValueFromMapTo(
							fieldMetadata.mapTo,
							defaultValueContext,
							engineFieldItemOptions,
						);

						const extraMatterDetails =
							fieldMetadata.mapTo?.extraMappings && fieldMetadata.mapTo
								? fieldMetadata.mapTo.extraMappings
										.filter(
											(mapping) => mapping.purpose === ExtraMapToPurpose.LABEL,
										)
										.map((mapping) => {
											const newMapTo = {
												collection: fieldMetadata.mapTo.collection,
												field: mapping.path,
												conversionType: fieldMetadata.mapTo.conversionType,
											} as MapTo;
											const val = extractValueFromMapTo(
												newMapTo,
												defaultValueContext,
												engineFieldItemOptions,
											);
											return getDisplayValue(val, engineFieldItemOptions);
										})
										.filter((val) => val && val !== '')
								: [];

						items.push({
							questionId: answer.questionId,
							formQuestion:
								answer.label || fieldMetadata.mapTo?.field || 'Unknown',
							clientResponse,
							friendlyClientResponse: answer.displayValue || '-',
							matterDetails: String(matterDetails ?? '-'),
							friendlyMatterDetails: getDisplayValue(
								matterDetails,
								engineFieldItemOptions,
								extraMatterDetails,
								fieldMetadata.mapTo?.conversionType,
							),
							status: answer.status,
							clientId: formResponse.id,
							mapTo: fieldMetadata.mapTo,
							uploadOnlyConfig: fieldMetadata.uploadOnlyConfig,
							isSelected: false,
							sectionTitle: fieldMetadata.sectionTitle,
							sectionOrder: fieldMetadata.sectionOrder,
							fieldOrder: fieldMetadata.fieldOrder,
						});
					},
				);
			}
		});

		return items.sort((a, b) => {
			const questionA = a.formQuestion || '';
			const questionB = b.formQuestion || '';
			return questionA.localeCompare(questionB);
		});
	},
);

export const $reviewResponsesViewModelSelector = createSelector(
	[
		SelectedMatter.id,
		AllResponseItemsSelector,
		MatterClientIntakeFormState.getMostRecentActiveForm,
		FormOptionsSelector,
	],
	(
		matterId: string,
		allItems: ReviewResponseItem[],
		intakeForm: ClientIntakeForm | null,
		formOptions: FormOption[],
	): ReviewResponsesViewModel => {
		const formData: Record<string, FormViewData> = {};

		formOptions.forEach((formOption) => {
			const formId = formOption.formId;
			const formItems = allItems.filter((item) => item.clientId === formId);

			const itemsByStatus = {
				all: formItems.filter((item) => !!item.mapTo),
				[FieldResponseStatus.READY_TO_REVIEW]: formItems.filter(
					(item) => item.status === FieldResponseStatus.READY_TO_REVIEW,
				),
				[FieldResponseStatus.ADDED_TO_MATTER]: formItems.filter(
					(item) => item.status === FieldResponseStatus.ADDED_TO_MATTER,
				),
				[FieldResponseStatus.MISSING_RESPONSES]: formItems.filter(
					(item) => item.status === FieldResponseStatus.MISSING_RESPONSES,
				),
				[FieldResponseStatus.CUSTOM]: formItems.filter(
					(item) => item.status === FieldResponseStatus.CUSTOM,
				),
			};

			const sectionsByStatus: Record<string, IntakeResponseSection[]> = {};
			Object.keys(itemsByStatus).forEach((status) => {
				// Use new version that doesn't need intakeForm.forms or formId
				sectionsByStatus[status] = groupItemsIntoSections(
					itemsByStatus[status],
				);
			});

			const counts = {
				all: formItems.length,
				[FieldResponseStatus.READY_TO_REVIEW]:
					itemsByStatus[FieldResponseStatus.READY_TO_REVIEW].length,
				[FieldResponseStatus.ADDED_TO_MATTER]:
					itemsByStatus[FieldResponseStatus.ADDED_TO_MATTER].length,
				[FieldResponseStatus.MISSING_RESPONSES]:
					itemsByStatus[FieldResponseStatus.MISSING_RESPONSES].length,
				[FieldResponseStatus.CUSTOM]:
					itemsByStatus[FieldResponseStatus.CUSTOM].length,
			};

			formData[formId] = {
				formInfo: formOption,
				itemsByStatus,
				sectionsByStatus,
				counts,
			};
		});

		return {
			matterId,
			formOptions,
			intakeFormId: intakeForm?.id ?? null, // ONLY extract the ID
			formData,
		};
	},
);
